import Quill from 'quill';
import { CELL_MIN_WIDTH, CSS_COLUMN_RESIZE_HANDLE, CSS_COLUMN_RESIZE_HANDLE_ACTIVE, CSS_ROW_RESIZE_HANDLE, CSS_ROW_RESIZE_HANDLE_ACTIVE, HANDLE_CENTER_OFFSET, ROW_MIN_HEIGHT, getTableContentRect } from './types';
/**
 * Clamps two adjacent column widths so neither goes below CELL_MIN_WIDTH
 * while keeping their combined width constant.
 */
function clampColumnWidths(leftWidth, rightWidth, combinedWidth) {
    if (leftWidth < CELL_MIN_WIDTH) {
        return [CELL_MIN_WIDTH, combinedWidth - CELL_MIN_WIDTH];
    }
    if (rightWidth < CELL_MIN_WIDTH) {
        return [combinedWidth - CELL_MIN_WIDTH, CELL_MIN_WIDTH];
    }
    return [leftWidth, rightWidth];
}
export class ResizeManager {
    constructor(editor, editorContainer, callbacks) {
        this.columnHandles = [];
        this.activeColumnTable = null;
        this.rowHandles = [];
        this.activeRowTable = null;
        this.editor = editor;
        this.editorContainer = editorContainer;
        this.callbacks = callbacks;
    }
    destroy() {
        this.removeColumnHandles();
        this.removeRowHandles();
    }
    repositionAll(table) {
        if (this.columnHandles.length > 0)
            this.repositionColumnHandles(table);
        if (this.rowHandles.length > 0)
            this.repositionRowHandles(table);
    }
    // --- Column resize handles ---
    attachColumnHandles(table) {
        if (this.activeColumnTable === table && this.columnHandles.length > 0) {
            this.repositionColumnHandles(table);
            return;
        }
        this.removeColumnHandles();
        this.activeColumnTable = table;
        const firstRow = table.querySelector('tr');
        if (!firstRow)
            return;
        const cells = firstRow.querySelectorAll('td');
        if (cells.length < 2)
            return;
        for (let i = 0; i < cells.length - 1; i++) {
            const handle = document.createElement('div');
            handle.className = CSS_COLUMN_RESIZE_HANDLE;
            this.editorContainer.appendChild(handle);
            this.columnHandles.push(handle);
            this.addColumnResizeHandler(handle, table, i);
        }
        this.repositionColumnHandles(table);
    }
    removeColumnHandles() {
        this.columnHandles.forEach((h) => h.remove());
        this.columnHandles = [];
        this.activeColumnTable = null;
    }
    // --- Row resize handles ---
    attachRowHandles(table) {
        if (this.activeRowTable === table && this.rowHandles.length > 0) {
            this.repositionRowHandles(table);
            return;
        }
        this.removeRowHandles();
        this.activeRowTable = table;
        const rows = table.querySelectorAll('tr');
        if (rows.length < 2)
            return;
        for (let i = 0; i < rows.length - 1; i++) {
            const handle = document.createElement('div');
            handle.className = CSS_ROW_RESIZE_HANDLE;
            this.editorContainer.appendChild(handle);
            this.rowHandles.push(handle);
            this.addRowResizeHandler(handle, table, i);
        }
        this.repositionRowHandles(table);
    }
    removeRowHandles() {
        this.rowHandles.forEach((h) => h.remove());
        this.rowHandles = [];
        this.activeRowTable = null;
    }
    // --- Positioning ---
    repositionColumnHandles(table) {
        const containerRect = this.editorContainer.getBoundingClientRect();
        const contentRect = getTableContentRect(table);
        const firstRow = table.querySelector('tr');
        if (!firstRow)
            return;
        const cells = firstRow.querySelectorAll('td');
        for (let i = 0; i < this.columnHandles.length && i < cells.length - 1; i++) {
            const cellRect = cells[i].getBoundingClientRect();
            const handle = this.columnHandles[i];
            handle.style.top = (contentRect.top - containerRect.top) + 'px';
            handle.style.left = (cellRect.right - containerRect.left - HANDLE_CENTER_OFFSET) + 'px';
            handle.style.height = contentRect.height + 'px';
        }
    }
    repositionRowHandles(table) {
        const containerRect = this.editorContainer.getBoundingClientRect();
        const contentRect = getTableContentRect(table);
        const rows = table.querySelectorAll('tr');
        for (let i = 0; i < this.rowHandles.length && i < rows.length - 1; i++) {
            const rowRect = rows[i].getBoundingClientRect();
            const handle = this.rowHandles[i];
            handle.style.left = (contentRect.left - containerRect.left) + 'px';
            handle.style.top = (rowRect.bottom - containerRect.top - HANDLE_CENTER_OFFSET) + 'px';
            handle.style.width = contentRect.width + 'px';
        }
    }
    // --- Drag handler ---
    createDragHandler(handle, table, activeClass, dragCallbacks) {
        let dragging = false;
        const handleDrag = (e) => {
            e.preventDefault();
            if (!dragging)
                return;
            dragCallbacks.onDrag(e);
        };
        const handleMouseup = (e) => {
            e.preventDefault();
            document.removeEventListener('mousemove', handleDrag, false);
            document.removeEventListener('mouseup', handleMouseup, false);
            if (dragging) {
                this.editor.update(Quill.sources.USER);
                this.callbacks.onContentChange();
                dragCallbacks.onEnd();
                setTimeout(() => {
                    this.repositionColumnHandles(table);
                    this.repositionRowHandles(table);
                    this.callbacks.positionToolbar(table);
                }, 10);
            }
            handle.classList.remove(activeClass);
            this.callbacks.showToolbar();
            this.callbacks.refreshSelectionLines();
            dragging = false;
        };
        handle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!dragCallbacks.onStart(e))
                return;
            dragging = true;
            handle.classList.add(activeClass);
            this.callbacks.hideToolbar();
            this.callbacks.hideSelectionLines();
            document.addEventListener('mousemove', handleDrag, false);
            document.addEventListener('mouseup', handleMouseup, false);
        }, false);
    }
    // --- Specific resize handlers ---
    addColumnResizeHandler(handle, table, colIndex) {
        let initialMouseX = 0;
        let leftColumnInitialWidth = 0;
        let rightColumnInitialWidth = 0;
        let leftColBlot = null;
        let rightColBlot = null;
        this.createDragHandler(handle, table, CSS_COLUMN_RESIZE_HANDLE_ACTIVE, {
            onStart: (e) => {
                const firstRow = table.querySelector('tr');
                const cells = firstRow ? firstRow.querySelectorAll('td') : [];
                if (cells.length <= colIndex + 1)
                    return false;
                leftColumnInitialWidth = cells[colIndex].getBoundingClientRect().width;
                rightColumnInitialWidth = cells[colIndex + 1].getBoundingClientRect().width;
                initialMouseX = e.clientX;
                const tableContainerRef = Quill.find(table);
                if (tableContainerRef) {
                    const colGroup = tableContainerRef.colGroup();
                    leftColBlot = colGroup.children.at(colIndex);
                    rightColBlot = colGroup.children.at(colIndex + 1);
                }
                return !!leftColBlot && !!rightColBlot;
            },
            onDrag: (e) => {
                if (!leftColBlot || !rightColBlot)
                    return;
                const delta = e.clientX - initialMouseX;
                const combinedWidth = leftColumnInitialWidth + rightColumnInitialWidth;
                const [clampedLeft, clampedRight] = clampColumnWidths(leftColumnInitialWidth + delta, rightColumnInitialWidth - delta, combinedWidth);
                leftColBlot.format('width', clampedLeft);
                rightColBlot.format('width', clampedRight);
                this.repositionColumnHandles(table);
                this.repositionRowHandles(table);
            },
            onEnd: () => {
                initialMouseX = 0;
                leftColumnInitialWidth = 0;
                rightColumnInitialWidth = 0;
                leftColBlot = null;
                rightColBlot = null;
            },
        });
    }
    addRowResizeHandler(handle, table, rowIndex) {
        let initialMouseY = 0;
        let initialRowHeight = 0;
        this.createDragHandler(handle, table, CSS_ROW_RESIZE_HANDLE_ACTIVE, {
            onStart: (e) => {
                const rows = table.querySelectorAll('tr');
                if (rows.length <= rowIndex)
                    return false;
                initialRowHeight = rows[rowIndex].getBoundingClientRect().height;
                initialMouseY = e.clientY;
                return true;
            },
            onDrag: (e) => {
                const rows = table.querySelectorAll('tr');
                if (rows.length <= rowIndex)
                    return;
                const newHeight = Math.max(ROW_MIN_HEIGHT, initialRowHeight + (e.clientY - initialMouseY));
                const row = rows[rowIndex];
                row.style.height = newHeight + 'px';
                row.querySelectorAll('td').forEach((cell) => {
                    cell.style.height = newHeight + 'px';
                });
                this.repositionRowHandles(table);
            },
            onEnd: () => {
                initialMouseY = 0;
                initialRowHeight = 0;
            },
        });
    }
}
